import { PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PageSize } from '@/components/DataTable/PageSizeSelector'
import { useVeryLowRiskCustomers } from '@/hooks/queries/credit/useVeryLowRiskCustomers'
import { Button } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddVeryLowRiskCustomerDialog } from './-components/AddVeryLowRiskCustomerDialog'
import { VeryLowRiskCustomersDataTable } from './-components/VeryLowRiskCustomersDataTable'

export const Route = createFileRoute('/_auth/_modules/credit/operations/very-low-risk-customers/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation('credit')
  const query = useVeryLowRiskCustomers()

  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(15)
  const [isAddOpen, setIsAddOpen] = useState(false)

  const allRows = query.data ?? []
  const normalizedKeyword = keyword.trim().toLowerCase()
  const filteredRows = normalizedKeyword
    ? allRows.filter(
        (row) =>
          row.erp.toLowerCase().includes(normalizedKeyword) ||
          (row.name ?? '').toLowerCase().includes(normalizedKeyword),
      )
    : allRows

  const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize)

  const existingErps = new Set(allRows.map((row) => row.erp))

  const onPageChange = (nextPageIndex: number) => setPage(nextPageIndex + 1)
  const onPageSizeChange = (nextPageSize: PageSize) => {
    setPageSize(nextPageSize)
    setPage(1)
  }

  // Filtering should always land the user back on the first page of results.
  const setKeywordAndResetPage: React.Dispatch<React.SetStateAction<string>> = (value) => {
    setKeyword(value)
    setPage(1)
  }

  return (
    <PageContainer>
      <Spread>
        <PageHeader>{t('veryLowRiskCustomers.pageHeader')}</PageHeader>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <VeryLowRiskCustomersDataTable
            data={pagedRows}
            rowCount={filteredRows.length}
            allErps={filteredRows.map((row) => row.erp)}
            isPending={query.isPending}
            error={query.error}
            page={page}
            pageSize={pageSize}
            search={{
              value: keyword,
              setValue: setKeywordAndResetPage,
              placeholder: t('veryLowRiskCustomers.searchPlaceholder'),
            }}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            toolbarActions={
              <PermissionsGuard required={['credit_very_low_risk_customers_create']}>
                <Button type="button" onClick={() => setIsAddOpen(true)}>
                  <PlusIcon className="size-4" />
                  {t('veryLowRiskCustomers.addCustomer')}
                </Button>
              </PermissionsGuard>
            }
          />
        </CardContent>
      </Card>

      <AddVeryLowRiskCustomerDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        existingErps={existingErps}
      />
    </PageContainer>
  )
}
